/**
 * Admin Settings Component
 *
 * Refactored to use modular sub-components for improved maintainability.
 * All state management is handled by the useAdminSettings hook.
 */
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../ui/tabs';
import { Palette, Settings, Zap, FolderTree, Scale, ShieldCheck, ShoppingCart, UserPlus, TrendingUp, Bell, LayoutDashboard, BarChart3, Users, Upload, LifeBuoy, LineChart, PackageCheck } from 'lucide-react';
import PerformanceSettings from '../settings/PerformanceSettings';
import { useAuth } from '../../contexts/AuthContext';
import { useMenuSettings } from '../../contexts/MenuSettingsContext';

// Import modular components
import {
  useAdminSettings,
  BrandingTab,
  PlatformTab,
  FeaturesTab,
  DashboardTabsTab,
  PurchasingTabsTab,
  QualityTabsTab,
  SupplierCardTab,
  SupplierProfileLayoutTab,
  HelpLayoutTab,
  DataImportTilesTab,
  HierarchyTab,
  LegalTab,
  QualityTab,
  PurchasingTab,
  ForecastSettingsTab,
  OnboardingTab,
  NotificationDefaultsTab,
  AnalyticsTab,
  CallOffConfigTab,
} from './settings';

export default function AdminSettings() {
  const { user } = useAuth();
  const isSuperuser = Boolean(user?.is_superuser);
  const { isFeatureEnabled } = useMenuSettings();
  // Call-off config tab is only relevant when this customer has the
  // per-supplier call-off flag enabled on at least one relationship.
  const showCallOffConfig = isFeatureEnabled('switch_call_off_tab');
  const {
    // Theme state
    themeSettings,
    setThemeSettings,

    // Platform state
    platformSettings,
    setPlatformSettings,

    // Barratt flags
    barrattFlags,
    setBarrattFlags,

    // Dashboard tab visibility (CR-4441)
    dashboardTabs,
    setDashboardTabs,
    supplierDashboardTabs,
    setSupplierDashboardTabs,
    purchasingTabs,
    setPurchasingTabs,
    savePurchasingTabs,
    qualityTabs,
    setQualityTabs,
    saveQualityTabs,
    supplierCardFields,
    setSupplierCardFields,
    saveSupplierCardFields,
    dataImportTiles,
    setDataImportTiles,
    saveDataImportTiles,

    // File upload
    saving,

    // Actions
    saveThemeSettings,
    savePlatformSettings,
    saveDashboardTabs,
    handleFileUpload,
    resetColorsToDefault,
  } = useAdminSettings();

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Admin Settings</h1>
          <p className="text-muted-foreground mt-2">
            Manage platform configuration, branding, and feature toggles
          </p>
        </div>
        <div className="flex items-center gap-2 text-sm text-muted-foreground">
          <span className="font-medium">Scope:</span>
          <span className="px-2 py-1 bg-primary/10 text-primary rounded text-xs font-medium">
            Platform-wide
          </span>
          <span className="text-xs">(Group/Company level overrides available in Hierarchy tab)</span>
        </div>
      </div>

      {/* Settings Tabs */}
      <Tabs defaultValue="platform" className="space-y-4">
        {/* CR-4816: 15-16 tabs crammed into equal grid columns collided on all
            reasonable widths — labels clipped and overlapped icons. Switch to
            an inline-flex bar with horizontal scroll so each tab sizes to its
            own content and the row overflows cleanly. */}
        <TabsList className="inline-flex w-full items-center justify-start gap-1 overflow-x-auto whitespace-nowrap">
          <TabsTrigger value="branding">
            <Palette className="h-4 w-4 mr-2" />
            Branding
          </TabsTrigger>
          <TabsTrigger value="dashboard-tabs">
            <LayoutDashboard className="h-4 w-4 mr-2" />
            Dashboard
          </TabsTrigger>
          <TabsTrigger value="supplier-card">
            <Users className="h-4 w-4 mr-2" />
            Supplier Card
          </TabsTrigger>
          <TabsTrigger value="supplier-profile">
            <Users className="h-4 w-4 mr-2" />
            Supplier Profile
          </TabsTrigger>
          <TabsTrigger value="data-import">
            <Upload className="h-4 w-4 mr-2" />
            Data Import
          </TabsTrigger>
          <TabsTrigger value="platform">
            <Settings className="h-4 w-4 mr-2" />
            Platform
          </TabsTrigger>
          <TabsTrigger value="features">
            <Zap className="h-4 w-4 mr-2" />
            Features
          </TabsTrigger>
          <TabsTrigger value="quality">
            <ShieldCheck className="h-4 w-4 mr-2" />
            Quality
          </TabsTrigger>
          <TabsTrigger value="purchasing">
            <ShoppingCart className="h-4 w-4 mr-2" />
            Purchasing
          </TabsTrigger>
          {showCallOffConfig && (
            <TabsTrigger value="call-off-config">
              <PackageCheck className="h-4 w-4 mr-2" />
              Call-Off
            </TabsTrigger>
          )}
          <TabsTrigger value="forecast">
            <LineChart className="h-4 w-4 mr-2" />
            Forecast
          </TabsTrigger>
          <TabsTrigger value="onboarding">
            <UserPlus className="h-4 w-4 mr-2" />
            Onboarding
          </TabsTrigger>
          <TabsTrigger value="hierarchy">
            <FolderTree className="h-4 w-4 mr-2" />
            Hierarchy
          </TabsTrigger>
<TabsTrigger value="legal">
            <Scale className="h-4 w-4 mr-2" />
            Legal
          </TabsTrigger>
          <TabsTrigger value="notifications">
            <Bell className="h-4 w-4 mr-2" />
            Notifications
          </TabsTrigger>
          <TabsTrigger value="performance">
            <TrendingUp className="h-4 w-4 mr-2" />
            Performance
          </TabsTrigger>
          <TabsTrigger value="analytics">
            <BarChart3 className="h-4 w-4 mr-2" />
            Analytics
          </TabsTrigger>
          {isSuperuser && (
            <TabsTrigger value="help-layout">
              <LifeBuoy className="h-4 w-4 mr-2" />
              Help
            </TabsTrigger>
          )}
        </TabsList>

        {/* BRANDING TAB */}
        <TabsContent value="branding">
          <BrandingTab
            themeSettings={themeSettings}
            saving={saving}
            onThemeSettingsChange={setThemeSettings}
            onSave={saveThemeSettings}
            onResetColors={resetColorsToDefault}
            onFileUpload={handleFileUpload}
          />
        </TabsContent>

        {/* DASHBOARD TABS TAB (CR-4441) */}
        <TabsContent value="dashboard-tabs">
          <DashboardTabsTab
            dashboardTabs={dashboardTabs}
            supplierDashboardTabs={supplierDashboardTabs}
            saving={saving}
            onDashboardTabsChange={setDashboardTabs}
            onSupplierDashboardTabsChange={setSupplierDashboardTabs}
            onSave={saveDashboardTabs}
          />
        </TabsContent>

        {/* SUPPLIER CARD TAB */}
        <TabsContent value="supplier-card">
          <SupplierCardTab
            supplierCardFields={supplierCardFields}
            saving={saving}
            onSupplierCardFieldsChange={setSupplierCardFields}
            onSave={saveSupplierCardFields}
          />
        </TabsContent>

        {/* SUPPLIER PROFILE LAYOUT TAB */}
        <TabsContent value="supplier-profile">
          <SupplierProfileLayoutTab />
        </TabsContent>

        {/* HELP & SUPPORT LAYOUT TAB — superuser-only */}
        {isSuperuser && (
          <TabsContent value="help-layout">
            <HelpLayoutTab />
          </TabsContent>
        )}

        {/* DATA IMPORT TAB */}
        <TabsContent value="data-import">
          <DataImportTilesTab
            dataImportTiles={dataImportTiles}
            saving={saving}
            onDataImportTilesChange={setDataImportTiles}
            onSave={saveDataImportTiles}
          />
        </TabsContent>

        {/* PLATFORM TAB */}
        <TabsContent value="platform">
          <PlatformTab
            platformSettings={platformSettings}
            saving={saving}
            onPlatformSettingsChange={setPlatformSettings}
            onSave={savePlatformSettings}
            purchasingTabs={purchasingTabs}
            onPurchasingTabsChange={setPurchasingTabs}
            onSavePurchasingTabs={savePurchasingTabs}
            qualityTabs={qualityTabs}
            onQualityTabsChange={setQualityTabs}
            onSaveQualityTabs={saveQualityTabs}
          />
        </TabsContent>

        {/* FEATURES TAB */}
        <TabsContent value="features">
          <FeaturesTab
            platformSettings={platformSettings}
            barrattFlags={barrattFlags}
            saving={saving}
            onPlatformSettingsChange={setPlatformSettings}
            onBarrattFlagsChange={setBarrattFlags}
            onSave={savePlatformSettings}
          />
        </TabsContent>

        {/* QUALITY TAB */}
        <TabsContent value="quality">
          <QualityTab />
          <div className="mt-6">
            <QualityTabsTab
              qualityTabs={qualityTabs}
              saving={saving}
              onQualityTabsChange={setQualityTabs}
              onSave={saveQualityTabs}
            />
          </div>
        </TabsContent>

        {/* PURCHASING TAB */}
        <TabsContent value="purchasing">
          <PurchasingTab />
          <div className="mt-6">
            <PurchasingTabsTab
              purchasingTabs={purchasingTabs}
              saving={saving}
              onPurchasingTabsChange={setPurchasingTabs}
              onSave={savePurchasingTabs}
            />
          </div>
        </TabsContent>

        {/* CALL-OFF CONFIG TAB */}
        {showCallOffConfig && (
          <TabsContent value="call-off-config">
            <CallOffConfigTab />
          </TabsContent>
        )}

        {/* FORECAST TAB */}
        <TabsContent value="forecast">
          <ForecastSettingsTab
            barrattFlags={barrattFlags}
            saving={saving}
            onBarrattFlagsChange={setBarrattFlags}
            onSave={savePlatformSettings}
          />
        </TabsContent>

        {/* ONBOARDING TAB */}
        <TabsContent value="onboarding">
          <OnboardingTab
            onboardingEnabled={platformSettings.supplierOnboardingEnabled}
            onToggleOnboarding={(enabled) => {
              setPlatformSettings({ ...platformSettings, supplierOnboardingEnabled: enabled });
              savePlatformSettings();
            }}
          />
        </TabsContent>

        {/* HIERARCHY TAB */}
        <TabsContent value="hierarchy">
          <HierarchyTab />
        </TabsContent>

{/* LEGAL TAB */}
        <TabsContent value="legal">
          <LegalTab />
        </TabsContent>

        {/* NOTIFICATIONS TAB */}
        <TabsContent value="notifications" className="space-y-4">
          <NotificationDefaultsTab
            platformSettings={platformSettings}
            saving={saving}
            onPlatformSettingsChange={setPlatformSettings}
            onSave={savePlatformSettings}
          />
        </TabsContent>

        {/* PERFORMANCE TAB */}
        <TabsContent value="performance" className="space-y-4">
          <PerformanceSettings />
        </TabsContent>

        {/* ANALYTICS TAB — toggle dataset visibility per customer */}
        <TabsContent value="analytics" className="space-y-4">
          <AnalyticsTab />
        </TabsContent>

      </Tabs>
    </div>
  );
}
